<script setup lang="ts">
import { ref } from 'vue'
import FileUpload from '../form/FileUpload.vue'
import TextInput from '../form/TextInput.vue'

interface Props {
  type?: 'info' | 'warning' | 'error'
  title?: string
  message: string
  show: boolean
  showCancel?: boolean
  showAttachment?: boolean
  showRemarks?: boolean
  requireAttachment?: boolean
  requireRemarks?: boolean
  confirmText?: string
  cancelText?: string
  path?: string
  showInput?: boolean
  requireInput?: boolean
  inputCaption?: string
  inputType?: 'text' | 'number'
  extraButton?: { text: string, event: string } // Extra button prop
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  showCancel: false,
  confirmText: 'OK',
  cancelText: 'Cancel',
  extraButton: undefined,
  inputType: 'text',
  path: ''
})

const attachment = ref('')
const remarks = ref('')
const input = ref('')

defineEmits<{
  (e: 'close'): void
  (e: 'confirm', remarks: string, file: string, input: string | null): void
  (e: 'extraButtonClick'): void
  (e: 'cancel'): void
  (e: 'fileUploaded', file: File | null): void
  (e: string): void // Allows emitting dynamic events
}>()

const getIcon = () => {
  switch (props.type) {
    case 'info':
      return { icon: 'ℹ️', bgColor: 'bg-blue-100 dark:bg-blue-900/50', textColor: 'text-blue-800 dark:text-blue-200' }
    case 'warning':
      return { icon: '⚠️', bgColor: 'bg-yellow-100 dark:bg-yellow-900/50', textColor: 'text-yellow-800 dark:text-yellow-200' }
    case 'error':
      return { icon: '❌', bgColor: 'bg-red-100 dark:bg-red-900/50', textColor: 'text-red-800 dark:text-red-200' }
  }
}

</script>

<template>
  <Transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition ease-in duration-150"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div 
      v-if="show"
      class="fixed inset-0 flex items-center justify-center z-[1000] p-4 bg-black/50"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <!-- Header -->
        <div 
          class="p-4 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700"
          :class="[getIcon().bgColor]"
        >
          <div class="text-2xl">{{ getIcon().icon }}</div>
          <h3 
            class="text-lg font-semibold"
            :class="[getIcon().textColor]"
          >
            {{ title || type.charAt(0).toUpperCase() + type.slice(1) }}
          </h3>
        </div>

        <!-- Message -->
        <div class="p-6">
          <p class="text-gray-700 dark:text-gray-300" v-html="message">
          </p>
        </div>

        <div class="p-6">
          <FileUpload label="Attachment" v-model="attachment" v-if="showAttachment" ref="fileInput" :multiple="false" :path="path"/>
          <textarea v-model="remarks" v-if="showRemarks" :rows="4" class="w-full p-2 border border-gray-200 dark:border-gray-700 rounded-lg" placeholder="Remarks"></textarea>
          <TextInput v-if="showInput || requireInput" :type="inputType" v-model="input" :label="inputCaption ? inputCaption : 'Remarks'" :placeholder="inputCaption ? inputCaption : 'Remarks'" />
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg flex justify-end gap-3">
          <button
            v-if="showCancel"
            @click="$emit('cancel')"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            {{ cancelText }}
          </button>
          
          <!-- Extra Button -->
          <button
            v-if="extraButton"
            @click="$emit(extraButton.event)"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-300 dark:bg-gray-700 border border-gray-400 dark:border-gray-600 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-600"
          >
            {{ extraButton.text }}
          </button>

          <button
            @click="showCancel ? $emit('confirm', remarks, attachment, input) : $emit('close')"
            v-if="(!requireAttachment || attachment) && (!requireRemarks || remarks) && (!requireInput || input)"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>